export class Stdlib {

    /**
     * Creates a deep clone of the provided value. This method duplicates the entire structure of the input,
     * preserving the data while ensuring no shared references to the input object.
     *
     * @param {any} value - The value to be cloned. It can be of any type, including primitives, objects, and arrays.
     * @return {any} Returns a new deep copy of the input value. If the input is undefined or null, the method directly returns the input.
     */
    static clone<T>(value: T): T {
        if (value === undefined || value === null) {
            return value;
        } else {
            return JSON.parse(JSON.stringify(value));
        }
    }

    /**
     * This is Kotlin's `let` function.
     * Executes the given function with the value if it is not null or undefined and returns the result.
     * @param value The value to check.
     * @param code A function to apply to the value if it is not null or undefined.
     */
    static let<T, V>(value: T | undefined | null, code: (it: T) => V): V | undefined {
        return value == null ? undefined : code(value);
    }

    static maxDate(a: Date | undefined | null, b: Date | undefined | null): Date | undefined {
        if (a == null || b == null) {
            return a ?? b ?? undefined;
        } else {
            return a > b ? a : b;
        }
    }

    static deleteAllUndefinedOrNull(obj: any): any {
        for (const key in obj) {
            if (obj[key] === undefined || obj[key] === null) {
                delete obj[key];
            }
        }
        return obj;
    }

    /**
     * Debounces a function call by waiting for a specified number of milliseconds.
     *
     * https://stackoverflow.com/questions/79321123/debouncing-a-search-term-when-using-the-new-angular-resource-api
     * @param ms     The number of milliseconds to wait before calling the function.
     * @param signal An AbortSignal to cancel the operation if needed.
     */
    static debounceWaitWithAbortSignal(ms: number, signal: AbortSignal) {
        return new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => resolve(), ms);

            signal.addEventListener('abort', () => {
                clearTimeout(timeout);
                reject(new Error('Operation aborted'));
            }, { once: true });
        });
    }

    static difference(s1: Set<string>, s2: Set<string>): Set<string> {
        const difference = new Set<string>();
        for (const id of s1) {
            if (!s2.has(id)) {
                difference.add(id);
            }
        }
        return difference;
    }

    /**
     * Lets you determine the value returned by a query based on whether a specified expression is null or not null.
     * If expr1 is not null, then NVL2 returns expr2. If expr1 is null, then NVL2 returns expr3.
     */
    static nvl2<A, B, C>(expr1: A, expr2: B, expr3: C): B | C {
        if (expr1 != null) return expr2;
        else return expr3;
    }
}
